import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import moment from "moment";
import openhouseAPI from "../services/openhouse";
import {
  PeakTimesAddress,
  PeakTimesRequest,
  PeakTimesResponse,
  PeakTimesWeekdayKey,
} from "../types/PeakTimes";

type FormAddress = {
  streetAddress?: unknown;
  city?: unknown;
  state?: unknown;
  postalCode?: unknown;
  country?: unknown;
};

type RecommendationInput = FormAddress & {
  date?: unknown;
  isOffline: boolean;
};

const WEEKDAYS: PeakTimesWeekdayKey[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function normalizeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeCountry(value: unknown): "US" | "CA" | null {
  const country =
    typeof value === "object" && value !== null && "value" in value
      ? (value as { value?: unknown }).value
      : value;
  return country === "US" || country === "CA" ? country : null;
}

function normalizeInput(input: RecommendationInput): PeakTimesRequest | null {
  const address: Omit<PeakTimesAddress, "country"> = {
    street_address: normalizeString(input.streetAddress),
    city: normalizeString(input.city),
    state: normalizeString(input.state),
    postal_code: normalizeString(input.postalCode),
  };
  const country = normalizeCountry(input.country);
  const date =
    input.date instanceof Date || typeof input.date === "string"
      ? moment(input.date)
      : moment.invalid();

  if (
    !address.street_address ||
    !address.city ||
    !address.state ||
    !address.postal_code ||
    !country ||
    !date.isValid()
  ) {
    return null;
  }

  return {
    address: { ...address, country },
    targetDate: date.format("YYYY-MM-DD"),
    limit: 1,
  };
}

function formatLocalTime(value: string): string | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  const period = hours >= 12 ? "pm" : "am";
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${match[2]} ${period}`;
}

function recommendationFromResponse(
  response: PeakTimesResponse | undefined,
  targetDate: string
): string | null {
  if (
    !response ||
    response.status !== "recommendations_available" ||
    response.targetDate !== targetDate
  ) {
    return null;
  }

  const [year, month, day] = targetDate.split("-").map(Number);
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];
  const window = response.weekdays[weekday]?.recommendedStartWindows?.[0];
  if (!window) return null;

  const start = formatLocalTime(window.localStartTime);
  const end = formatLocalTime(window.localEndTime);
  return start && end ? `${start} - ${end}` : null;
}

export default function usePeakTimeRecommendation(input: RecommendationInput) {
  const request = useMemo(
    () => normalizeInput(input),
    [
      input.streetAddress,
      input.city,
      input.state,
      input.postalCode,
      input.country,
      input.date,
    ]
  );
  const requestKey = request ? JSON.stringify(request) : null;
  const [debouncedRequestKey, setDebouncedRequestKey] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!requestKey || input.isOffline) {
      setDebouncedRequestKey(null);
      return;
    }

    const timeout = setTimeout(() => setDebouncedRequestKey(requestKey), 350);
    return () => clearTimeout(timeout);
  }, [requestKey, input.isOffline]);

  const debouncedRequest = useMemo<PeakTimesRequest | null>(
    () => (debouncedRequestKey ? JSON.parse(debouncedRequestKey) : null),
    [debouncedRequestKey]
  );

  const { data, isLoading, isFetching } = useQuery(
    ["peakTimeRecommendation", debouncedRequestKey],
    async () => {
      const response = await openhouseAPI.post<PeakTimesResponse>(
        "/openhouse/peak-times",
        debouncedRequest
      );
      return response.data;
    },
    {
      enabled: Boolean(debouncedRequest && !input.isOffline),
      retry: false,
      cacheTime: 0,
    }
  );

  const isCurrentRequest = requestKey === debouncedRequestKey;
  const recommendation =
    isCurrentRequest && debouncedRequest?.targetDate && !isLoading && !isFetching
      ? recommendationFromResponse(data, debouncedRequest.targetDate)
      : null;

  return recommendation;
}
